import React, { FC, useState } from "react";
import { Button, Modal, Text, TextField, toast } from "@telia/teddy";
import "./CloneProdOrderModal.less";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const PLACEHOLDER = `{
    "rapid_order": {
        "cart": [
            ...
        ],
        ...
    }
}`;

const CloneProdOrderModal: FC<Props> = ({ open, onOpenChange }) => {
    const [value, setValue] = useState("");
    const [submitState, setSubmitState] = useState<"idle" | "sending" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleClose = () => {
        setValue("");
        setSubmitState("idle");
        setErrorMessage("");
        onOpenChange(false);
    };

    const handleSubmit = async () => {
        setSubmitState("sending");
        setErrorMessage("");

        let parsed: unknown;
        try {
            parsed = JSON.parse(value);
        } catch {
            setSubmitState("error");
            setErrorMessage("Ugyldig JSON. Sjekk at du har limt inn gyldig JSON.");
            return;
        }

        try {
            const response = await fetch("/shop/devtools/clone-prod-order/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(parsed),
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                const message =
                    typeof body.error === "string" ? body.error : `Forespørselen feilet med status ${response.status}`;
                setSubmitState("error");
                setErrorMessage(message);
                return;
            }

            toast.success("Ordre er oppdatert", { position: "top-right" });
            handleClose();
            window.location.reload();
        } catch {
            setSubmitState("error");
            setErrorMessage("Noe gikk galt. Sjekk nettverkstilkoblingen og prøv igjen.");
        }
    };

    const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && event.metaKey) {
            event.preventDefault();
            void handleSubmit();
        }
    };

    return (
        <Modal open={open} onOpenChange={(next) => !next && handleClose()}>
            <Modal.Overlay>
                <Modal.Content className="clone-prod-order-modal" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <Modal.Group variant="title">
                        <Modal.Title as="h3">Clone a prod order</Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" />
                    </Modal.Group>
                    <Modal.Group direction="column">
                        <Text>
                            Find a prod order in Splunk and paste in the content. The JSON can contain anything, as long
                            as a `rapid_order` (or `rapidOrder`) field exists somewhere in it. Only the rapidOrder part
                            is used, not the OCM part. The order content will be cloned, but with your orderRef, so you
                            can safely modify and experiement with the cloned order afterwards.
                        </Text>
                        <TextField>
                            <TextField.Textarea
                                autoFocus
                                rows={12}
                                placeholder={PLACEHOLDER}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={handleTextareaKeyDown}
                            />
                            {submitState === "error" && <TextField>{errorMessage}</TextField>}
                        </TextField>
                    </Modal.Group>
                    <Modal.Group>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={submitState === "sending"}
                            disabled={!value.trim() || submitState === "sending"}
                            mb="100"
                        >
                            Bekreft
                        </Button>
                        <Button variant="secondary" onClick={handleClose}>
                            Avbryt
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default CloneProdOrderModal;
